import { useCallback, useRef, useState } from 'react'

import { DueDateBadge } from '@/shared/components/common'
import { Popover } from '@/shared/components/ui'
import { cn } from '@/shared/lib/cn'
import { useI18n } from '@/shared/i18n'

import { DueDatePicker } from './DueDatePicker'

export interface DueDateCellProps {
  value: string | null
  onChange: (date: string | null) => void
  isDone?: boolean
  now?: Date
  /** Qué fecha es. Default: el vencimiento de una tarea. */
  label?: string
  /**
   * Sin semáforo: la fecha a secas. Para fechas que no vencen —el inicio de un
   * proyecto—, donde "hace 3 días" en rojo diría algo falso.
   */
  plain?: boolean
  align?: 'start' | 'end'
  className?: string
}

/**
 * El vencimiento, editable DESDE LA FILA.
 *
 * Cambiar una fecha es lo que más pasa en una lista de tareas, y estaba dos
 * clics más adentro: abrir la tarea, buscar el campo, cerrar el panel. La
 * fecha es lo único de la fila que se corrige todos los días, así que es lo
 * único que se edita sin salir de ella.
 *
 * **No es un `<input type="date">` en cada fila**: seis inputs en una lista
 * densa pesan más que las seis tareas, y la pantalla se convierte en un
 * formulario. Lo que se ve sigue siendo el badge —con su color y su "hace 3
 * días"—; el editor aparece sólo cuando alguien lo pide.
 */
export function DueDateCell({
  value,
  onChange,
  isDone,
  now,
  label,
  plain = false,
  align = 'end',
  className,
}: DueDateCellProps) {
  const { t, formatDate } = useI18n()
  const etiqueta = label ?? t('tasks.field.dueDateEdit')
  const [open, setOpen] = useState(false)
  const boton = useRef<HTMLButtonElement>(null)
  const cerrar = useCallback(() => setOpen(false), [])

  return (
    <div className={cn('shrink-0', className)}>
      <button
        ref={boton}
        type="button"
        onClick={() => setOpen((abierto) => !abierto)}
        aria-expanded={open}
        title={etiqueta}
        className={cn(
          'flex w-full items-center rounded-[var(--radius-control)] px-1 py-0.5',
          'transition-colors duration-[var(--motion-fast)] hover:bg-surface-2',
          align === 'end' ? 'justify-end' : 'justify-start',
        )}
      >
        {plain ? (
          <span className={cn('num text-xs', value ? 'text-text-2' : 'text-text-3')}>
            {value ? formatDate(value) : t('common.state.none')}
          </span>
        ) : (
          <DueDateBadge date={value} isDone={isDone} now={now} />
        )}
      </button>

      {/* Del lado de la celda: en la fila de una tarea vive al final. */}
      <Popover
        anchor={boton}
        open={open}
        onClose={cerrar}
        align={align}
        role="dialog"
        aria-label={etiqueta}
        className="p-2"
      >
        <DueDatePicker
          withShortcuts
          value={value}
          onChange={(fecha) => {
            onChange(fecha)
            setOpen(false)
          }}
        />
      </Popover>
    </div>
  )
}
